import { Link } from "react-router-dom";
import TextWithLabel from "../../../controls/TextWithLabel";

function formatDateTime(value) {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return {
      dateText: value,
      timeText: "",
    };
  }

  return {
    dateText: date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    }),
    timeText: date.toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
    }),
  };
}

function formatBarcodeSerial(value) {
  if (!value) return "N/A";
  const barcode = String(value);
  if (barcode.length <= 15) return barcode;
  return `...${barcode.slice(-15)}`;
}

function getActionBadgeClass(action) {
  const text = (action || "").toLowerCase();
  if (text.includes("remove") || text.includes("delete")) {
    return "bg-red-100 text-red-700";
  }
  if (text.includes("scan") || text.includes("creation")) {
    return "bg-green-100 text-green-700";
  }
  if (text.includes("adjust")) {
    return "bg-amber-100 text-amber-700";
  }
  return "bg-slate-100 text-slate-700";
}

function EventCard({ event }) {
  const shipping = event.context?.shipping;
  const orderItem = event.context?.order_item;
  const metadata = event.metadata;
  const eventDateTime = formatDateTime(event.created_at);

  return (
    <div className="card-custom p-4 border border-gray-100">
      <div className="flex flex-wrap items-center gap-2">
        <span
          className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${getActionBadgeClass(event.action)}`}
        >
          {(event.action || "unknown").replace(/_/g, " ")}
        </span>
        <span className="inline-flex rounded-full bg-purple-100 px-2.5 py-1 text-xs font-semibold text-purple-700">
          {(event.type || "unknown").replace(/_/g, " ")}
        </span>
        <div className="ml-auto text-right">
          <p className="text-xs font-semibold text-gray-700">
            {eventDateTime.dateText}
          </p>
          {eventDateTime.timeText ? (
            <p className="text-xs text-gray-500">{eventDateTime.timeText}</p>
          ) : null}
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <TextWithLabel
          label="Barcode Serial"
          text={formatBarcodeSerial(event.barcode)}
        />
        <TextWithLabel label="Operator" text={event.operator?.label || "N/A"} />
        <TextWithLabel
          label="Amount"
          text={event.amount != null ? String(event.amount) : "N/A"}
        />
        <TextWithLabel label="Reason" text={event.reason || "N/A"} />
      </div>

      {shipping || orderItem ? (
        <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-3">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Context
            </p>
            {shipping?.id ? (
              <Link
                className="rounded-md bg-main-purple px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-indigo-600"
                to={`/shippings/${shipping.id}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                View
              </Link>
            ) : null}
          </div>
          <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <TextWithLabel label="PO Number" text={shipping?.po_number || "N/A"} />
            <TextWithLabel
              label="Invoice Number"
              text={shipping?.invoice_number || "N/A"}
            />
            <TextWithLabel
              label="Order Item Product"
              text={orderItem?.product_name || "N/A"}
            />
          </div>
        </div>
      ) : null}

      {/* {metadata ? (
        <div className="mt-4 rounded-lg border border-gray-200 bg-white p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Metadata
          </p>
          <pre className="mt-2 overflow-auto rounded-md bg-slate-900 p-3 text-xs text-slate-100">
            {JSON.stringify(metadata, null, 2)}
          </pre>
        </div>
      ) : null} */}
    </div>
  );
}

export default function PackagingBarcodeLookupResult({
  itemType,
  item,
  manufacturedProduct,
  histories,
}) {
  const itemDateTime = formatDateTime(item?.created_at || item?.packaging_date);
  const lastScanDateTime = formatDateTime(histories?.[0]?.created_at);
  const statusText =
    item?.status_label ||
    (item?.status != null ? String(item.status) : "N/A");

  return (
    <div className="space-y-4 py-4">
      <div className="card-custom rounded-xl border border-gray-200 p-5">
        <div className="flex items-center gap-2">
          <h2 className="text-base font-semibold text-gray-900">Resolved Item</h2>
          <span className="rounded-full bg-main-purple/10 px-2.5 py-1 text-xs font-semibold uppercase text-main-purple">
            {itemType || "unknown"}
          </span>
        </div>
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <TextWithLabel label="ID" text={item?.id ?? "N/A"} />
          <TextWithLabel
            label="Barcode Serial"
            text={formatBarcodeSerial(item?.barcode || item?.shipping_barcode)}
          />
          <TextWithLabel label="Lot Number" text={item?.lot_number || "N/A"} />
          <TextWithLabel
            label="Created At"
            text={
              itemDateTime.timeText
                ? `${itemDateTime.dateText}, ${itemDateTime.timeText}`
                : itemDateTime.dateText
            }
          />
          <TextWithLabel label="Product Name" text={item?.product_name || "N/A"} />
          <TextWithLabel label="Status" text={statusText} />
          <TextWithLabel
            label="Last Scan Date"
            text={
              lastScanDateTime.timeText
                ? `${lastScanDateTime.dateText}, ${lastScanDateTime.timeText}`
                : lastScanDateTime.dateText
            }
          />
          <TextWithLabel
            label="Order Item ID"
            text={item?.order_item_id != null ? item.order_item_id : "N/A"}
          />
        </div>

        <div className="mt-4">
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Production Run
              </p>
              {manufacturedProduct?.id ? (
                <Link
                  className="rounded-md bg-main-purple px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-indigo-600"
                  to={`/manufactured-products/${manufacturedProduct.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View
                </Link>
              ) : null}
            </div>
            <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-3">
              <TextWithLabel
                label="Manufactured Product"
                text={manufacturedProduct?.product_name || "N/A"}
              />
              <TextWithLabel
                label="Manufactured Lot"
                text={manufacturedProduct?.lot_number || "N/A"}
              />
              <TextWithLabel label="PLU" text={manufacturedProduct?.plu || "N/A"} />
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-gray-900">History Timeline</h3>
          <span className="text-sm text-gray-500">
            {histories.length} event{histories.length === 1 ? "" : "s"}
          </span>
        </div>
        {histories.length === 0 ? (
          <div className="card-custom rounded-xl border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
            No history rows were returned for this barcode.
          </div>
        ) : (
          histories.map((event, index) => (
            <EventCard key={event.id || `${event.barcode}-${index}`} event={event} />
          ))
        )}
      </div>
    </div>
  );
}
